const PLACEHOLDER_ORIGIN = "https://next-path.invalid"

/**
 * Client-side mirror of the authorize route's `safeNextPath`
 * (`@lib/auth/oidc`): only relative in-app paths are accepted (no
 * protocol-relative "//", no scheme, no backslash tricks), so a `next` value
 * carried in page state can never turn `window.location.href` or a social
 * callbackURL into an open redirect.
 *
 * `@lib/auth/oidc` is `server-only`, so client components re-validate through
 * this module instead of importing it; the authorize route re-validates again
 * server-side, which is the load-bearing gate.
 */
export const safeNextPath = (value: string | undefined | null) => {
  if (!value) return null
  // C0 controls, DEL and whitespace: the URL parser folds them away, so
  // `"/\n/evil.com"` resolves to `https://evil.com/` while still looking like
  // an in-app path. Reject before any structural check.
  if (/[\u0000-\u001f\u007f\s]/.test(value)) return null
  if (!value.startsWith("/")) return null
  if (value.startsWith("//")) return null
  if (value.includes("\\")) return null

  // Structural backstop: resolve against a fixed placeholder and require that
  // the value stays on it. Anything that can change scheme/host (even through
  // parser quirks not covered above) is not an in-app path.
  try {
    const parsed = new URL(value, PLACEHOLDER_ORIGIN)
    if (parsed.origin !== PLACEHOLDER_ORIGIN) return null
    if (!parsed.pathname.startsWith("/")) return null
    // `/..//evil.com` normalizes to the pathname `//evil.com`: still same-origin
    // when resolved against a base, but a caller that concatenates the raw value
    // onto an origin would hand the browser a protocol-relative URL. Reject it.
    if (parsed.pathname.startsWith("//")) return null
  } catch {
    return null
  }

  // Return the original value, not the resolved absolute URL: callers append
  // it to an origin they already trust.
  return value
}

/**
 * True only for an absolute https URL on a PayPal domain — the single external
 * redirect target the trial bind flow may navigate to (0.8.5 工单 08). The
 * approve URL is server-provided, but validating it before `window.location`
 * keeps a spoofed/compromised response from becoming an open redirect.
 */
export const isPaypalApprovalUrl = (value: string): boolean => {
  try {
    const url = new URL(value)
    if (url.protocol !== "https:") return false
    const host = url.hostname.toLowerCase()
    return host === "paypal.com" || host.endsWith(".paypal.com")
  } catch {
    return false
  }
}
